import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail, API, getToken } from "../../lib/api";
import { ArrowLeft, Save, Bold, Italic, Link as LinkIcon, Image as ImageIcon, X, Upload, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

const DEFAULT = {
  title: "",
  excerpt: "",
  body: "",
  category: "Fundamentals",
  cover_image: "",
  published: true,
  meta_description: "",
  read_minutes: 5,
  scheduled_publish_at: null,
};

// Convert an ISO-UTC string ↔ the local `<input type="datetime-local">` value.
// The browser input is naive-local, so we translate through the user's timezone
// on both directions. Any past/empty input yields null so the API can clear the
// schedule cleanly.
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(local) {
  if (!local) return null;
  const d = new Date(local);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

// Resolve a stored URL (which may be relative /api/media/... or absolute https://...) into a fully qualified one
function absUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  if (url.startsWith("/api/")) return `${base}${url}`;
  if (url.startsWith("/")) return `${base}${url}`;
  return url;
}

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const token = getToken();
  const res = await fetch(`${API}/admin/media`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!res.ok) {
    let msg = "Upload failed";
    try { const j = await res.json(); msg = j.detail || msg; } catch {}
    throw new Error(msg);
  }
  return res.json(); // {id, url, content_type, ...}
}

export default function AdminInsightEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(DEFAULT);
  const [loading, setLoading] = useState(isEdit);
  const [busy, setBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const [bodyBusy, setBodyBusy] = useState(false);
  const coverFileRef = useRef(null);
  const bodyFileRef = useRef(null);
  const bodyTextareaRef = useRef(null);
  const nav = useNavigate();

  // Managed focus/category list for the dropdown (+ inline add)
  const [categories, setCategories] = useState([]);
  const [addingCat, setAddingCat] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [catBusy, setCatBusy] = useState(false);

  useEffect(() => {
    api.get("/insight-categories").then((r) => setCategories(r.data?.categories || [])).catch(() => {});
  }, []);

  const addCategory = async () => {
    const name = newCat.trim();
    if (!name) return;
    setCatBusy(true);
    try {
      const r = await api.post("/admin/insight-categories", { name });
      setCategories(r.data?.categories || []);
      set("category", name);
      setNewCat("");
      setAddingCat(false);
      toast.success("Focus added.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not add focus.");
    } finally {
      setCatBusy(false);
    }
  };

  useEffect(() => {
    if (!isEdit) return;
    api
      .get(`/admin/insights/${id}`)
      .then((r) => setForm(r.data))
      .catch(() => toast.error("Could not load article."))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onCoverFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverBusy(true);
    try {
      const { url } = await uploadImage(file);
      set("cover_image", url);
      toast.success("Cover image uploaded.");
    } catch (err) {
      toast.error(err.message || "Upload failed.");
    } finally {
      setCoverBusy(false);
      if (coverFileRef.current) coverFileRef.current.value = "";
    }
  };

  const insertAtCursor = (insertText) => {
    const ta = bodyTextareaRef.current;
    if (!ta) {
      set("body", (form.body || "") + insertText);
      return;
    }
    const start = ta.selectionStart ?? form.body.length;
    const end = ta.selectionEnd ?? start;
    const next = form.body.slice(0, start) + insertText + form.body.slice(end);
    set("body", next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + insertText.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const wrapSelection = (before, after, placeholder) => {
    const ta = bodyTextareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? form.body.length;
    const end = ta.selectionEnd ?? start;
    const sel = form.body.slice(start, end) || placeholder;
    const next = form.body.slice(0, start) + before + sel + after + form.body.slice(end);
    set("body", next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length, start + before.length + sel.length);
    });
  };

  const insertLink = () => {
    const url = window.prompt("URL (e.g. https://example.com or /insights/another-article)", "https://");
    if (!url) return;
    wrapSelection("[", `](${url})`, "link text");
  };

  const onBodyImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBodyBusy(true);
    try {
      const { url } = await uploadImage(file);
      const alt = file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ");
      insertAtCursor(`\n\n![${alt}](${url})\n\n`);
      toast.success("Image inserted.");
    } catch (err) {
      toast.error(err.message || "Upload failed.");
    } finally {
      setBodyBusy(false);
      if (bodyFileRef.current) bodyFileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit) {
        await api.put(`/admin/insights/${id}`, form);
        toast.success("Article saved.");
      } else {
        await api.post(`/admin/insights`, form);
        toast.success("Article created.");
        nav(`/admin/insights`);
        return;
      }
      nav(`/admin/insights`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-[#447694]">Loading…</div>;

  return (
    <div className="p-6 md:p-10" data-testid="admin-insight-editor-page">
      <Link to="/admin/insights" className="inline-flex items-center gap-1 text-sm text-[#447694] hover:text-[#04273d]" data-testid="admin-editor-back-link">
        <ArrowLeft size={14} /> Back to articles
      </Link>
      <h1 className="font-display text-3xl md:text-4xl text-[#04273d] mt-3 font-semibold tracking-tight">
        {isEdit ? "Edit article" : "New article"}
      </h1>

      <form onSubmit={submit} className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="bnp-card p-6">
            <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">Title</label>
            <input required value={form.title} onChange={(e) => set("title", e.target.value)} className="bnp-input" data-testid="editor-title-input" />

            <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5 mt-4">Excerpt</label>
            <textarea required rows={3} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} className="bnp-input" data-testid="editor-excerpt-input" />

            <div className="mt-4">
              <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">Body</label>
              {/* Body editor toolbar */}
              <div className="flex items-center gap-1 flex-wrap bg-[#f7f7f7]/70 border border-[#9ac7f7]/30 rounded-t-2xl px-2 py-2" data-testid="editor-body-toolbar">
                <button type="button" onClick={() => wrapSelection("**", "**", "bold text")} className="btn-ghost text-xs px-2.5" data-testid="editor-body-bold">
                  <Bold size={12} /> Bold
                </button>
                <button type="button" onClick={() => wrapSelection("*", "*", "italic text")} className="btn-ghost text-xs px-2.5" data-testid="editor-body-italic">
                  <Italic size={12} /> Italic
                </button>
                <button type="button" onClick={() => wrapSelection("## ", "", "Heading")} className="btn-ghost text-xs px-2.5" data-testid="editor-body-heading">
                  H2
                </button>
                <button type="button" onClick={() => insertAtCursor("\n- ")} className="btn-ghost text-xs px-2.5" data-testid="editor-body-bullet">
                  • List
                </button>
                <span className="h-4 w-px bg-[#9ac7f7]/40 mx-1" />
                <button type="button" onClick={insertLink} className="btn-ghost text-xs px-2.5" data-testid="editor-body-link">
                  <LinkIcon size={12} /> Add link
                </button>
                <button
                  type="button"
                  onClick={() => bodyFileRef.current?.click()}
                  disabled={bodyBusy}
                  className="btn-ghost text-xs px-2.5"
                  data-testid="editor-body-image"
                >
                  {bodyBusy ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />} Upload image
                </button>
                <input ref={bodyFileRef} type="file" accept="image/*" onChange={onBodyImageFile} className="hidden" data-testid="editor-body-image-input" />
              </div>
              <textarea
                ref={bodyTextareaRef}
                required
                rows={18}
                value={form.body}
                onChange={(e) => set("body", e.target.value)}
                className="bnp-input rounded-t-none border-t-0 font-mono-brand text-sm"
                placeholder="Write your article here. Use the toolbar to add **bold**, *italic*, ## headings, lists, [links](https://...) and ![images](url)."
                data-testid="editor-body-input"
              />
              <p className="text-[11px] text-[#447694] mt-1.5">
                Supports markdown: <code className="bg-[#9ac7f7]/15 px-1 rounded">**bold**</code>, <code className="bg-[#9ac7f7]/15 px-1 rounded">*italic*</code>, <code className="bg-[#9ac7f7]/15 px-1 rounded">## Heading</code>, <code className="bg-[#9ac7f7]/15 px-1 rounded">- bullet</code>, <code className="bg-[#9ac7f7]/15 px-1 rounded">[text](url)</code>, <code className="bg-[#9ac7f7]/15 px-1 rounded">![alt](url)</code>.
              </p>
            </div>
          </div>
        </div>

        <aside className="space-y-5">
          <div className="bnp-card p-6" data-testid="editor-publish-card">
            <p className="font-display font-semibold text-[#04273d] mb-3">Publish settings</p>
            <label className="flex items-center gap-2 text-sm text-[#04273d]">
              <input
                type="checkbox"
                checked={!!form.published}
                onChange={(e) => set("published", e.target.checked)}
                className="h-4 w-4 accent-[#04273d]"
                data-testid="editor-published-checkbox"
              />
              Published (visible on public site)
            </label>

            {/* Scheduling - when a future time is set, the backend forces
                `published=false` on save and the 60-second scheduler flips
                it to Published automatically. Clearing the input removes any
                pending schedule. All times shown in the admin's browser
                timezone (typically UK time). */}
            <div className="mt-4 pt-4 border-t border-[#9ac7f7]/25">
              <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">
                Schedule publish (optional)
              </label>
              <input
                type="datetime-local"
                value={isoToLocalInput(form.scheduled_publish_at)}
                onChange={(e) => set("scheduled_publish_at", localInputToIso(e.target.value))}
                className="bnp-input text-sm"
                data-testid="editor-schedule-input"
              />
              {form.scheduled_publish_at ? (
                <div className="mt-2 flex items-center justify-between gap-3">
                  <p className="text-[11px] text-[#447694] leading-snug">
                    Auto-publishes on{" "}
                    <span className="font-mono-brand text-[#04273d]">
                      {new Date(form.scheduled_publish_at).toLocaleString("en-GB", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </span>
                    . Article stays as a draft until then.
                  </p>
                  <button
                    type="button"
                    onClick={() => set("scheduled_publish_at", null)}
                    className="btn-ghost text-[11px] whitespace-nowrap"
                    data-testid="editor-schedule-clear"
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <p className="text-[11px] text-[#447694] mt-2 leading-snug">
                  Leave empty to publish immediately (or keep as a manual draft).
                </p>
              )}
            </div>
          </div>

          {/* Cover image uploader */}
          <div className="bnp-card p-6" data-testid="editor-cover-card">
            <p className="font-display font-semibold text-[#04273d] mb-1">Cover image</p>
            <p className="text-[11px] text-[#447694] mb-3">JPG, PNG, WebP or SVG. Max 5 MB.</p>
            {form.cover_image ? (
              <div className="relative rounded-2xl overflow-hidden border border-[#9ac7f7]/30">
                <img src={absUrl(form.cover_image)} alt="Cover" className="w-full h-44 object-cover" data-testid="editor-cover-preview" />
                <button
                  type="button"
                  onClick={() => set("cover_image", "")}
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white text-[#04273d] rounded-full p-1.5 shadow"
                  aria-label="Remove cover image"
                  data-testid="editor-cover-remove"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => coverFileRef.current?.click()}
                disabled={coverBusy}
                className="w-full bg-[#f7f7f7] border-2 border-dashed border-[#9ac7f7]/50 hover:border-[#71edc9] rounded-2xl p-6 flex flex-col items-center gap-2 transition-colors"
                data-testid="editor-cover-upload-button"
              >
                {coverBusy ? <Loader2 size={20} className="animate-spin text-[#71edc9]" /> : <Upload size={20} className="text-[#447694]" />}
                <span className="font-display font-semibold text-sm text-[#04273d]">{coverBusy ? "Uploading…" : "Upload cover image"}</span>
                <span className="text-[11px] text-[#447694]">Click to choose a file</span>
              </button>
            )}
            <input ref={coverFileRef} type="file" accept="image/*" onChange={onCoverFile} className="hidden" data-testid="editor-cover-input" />
            <div className="mt-3">
              <label className="block text-[11px] text-[#447694] mb-1">Or paste an image URL</label>
              <input
                value={form.cover_image || ""}
                onChange={(e) => set("cover_image", e.target.value)}
                className="bnp-input text-sm"
                placeholder="https://… or /api/media/…"
                data-testid="editor-cover-url-input"
              />
            </div>
          </div>

          <div className="bnp-card p-6 space-y-4">
            <div>
              <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">Focus / Category</label>
              <select
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
                className="bnp-input"
                data-testid="editor-category-select"
              >
                {(categories.some((c) => c.toLowerCase() === (form.category || "").toLowerCase())
                  ? categories
                  : [form.category, ...categories].filter(Boolean)
                ).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>

              {!addingCat ? (
                <button
                  type="button"
                  onClick={() => setAddingCat(true)}
                  className="btn-ghost text-xs mt-2"
                  data-testid="editor-category-add-toggle"
                >
                  <Plus size={12} /> Add new focus
                </button>
              ) : (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    autoFocus
                    value={newCat}
                    onChange={(e) => setNewCat(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCategory(); } }}
                    placeholder="e.g. Trade Marks"
                    className="bnp-input text-sm"
                    data-testid="editor-category-new-input"
                  />
                  <button type="button" onClick={addCategory} disabled={catBusy} className="btn-secondary text-xs whitespace-nowrap px-3 py-2" data-testid="editor-category-add-button">
                    {catBusy ? "…" : "Add"}
                  </button>
                  <button type="button" onClick={() => { setAddingCat(false); setNewCat(""); }} className="btn-ghost text-xs" data-testid="editor-category-add-cancel">
                    Cancel
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">Read minutes</label>
              <input type="number" min="1" value={form.read_minutes || 5} onChange={(e) => set("read_minutes", parseInt(e.target.value || "5", 10))} className="bnp-input" data-testid="editor-read-minutes-input" />
            </div>
            <div>
              <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">Meta description (SEO)</label>
              <textarea rows={3} value={form.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} className="bnp-input" data-testid="editor-meta-input" />
            </div>
          </div>

          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="editor-save-button">
            <Save size={14} /> {busy ? "Saving…" : isEdit ? "Save changes" : "Create article"}
          </button>
        </aside>
      </form>
    </div>
  );
}
